import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { SupplierNote } from '@/types/fcc'
import { supplierNotesApi } from '@/services/supplierNotesService'
import { AuthState } from '@/types/authState'
import { ApiError } from '@/util/ApiError'

/**
 * Manual notes for the supplier currently open on the profile. The whole set is
 * fetched in one call and paged in the component, so there is no paging state
 * here.
 */
export const useFCCSupplierNotesStore = defineStore('fccSupplierNotes', () => {
  const notes = ref<SupplierNote[]>([])
  const loading = ref(false)
  const saving = ref(false)
  const error = ref<ApiError | null>(null)
  /**
   * Set when the API answers 404, which here means the supplier is not in the
   * FCC roster rather than that the read failed.
   */
  const merchantNotFound = ref(false)
  /**
   * Latest-wins guard. The profile swaps merchant without remounting, so a
   * slow read for the supplier just left can settle after the read for the one
   * now on screen. Only the newest request may touch state.
   */
  let fetchRequestId = 0

  const noteCount = computed(() => notes.value.length)

  /**
   * A note with no usable timestamp sorts last rather than poisoning the
   * comparator: an unparseable date yields NaN, and a comparator that returns
   * NaN leaves the whole order undefined, not just that one row.
   */
  function timeOf(note: SupplierNote): number {
    const time = new Date(note.CREATED_AT).getTime()
    return Number.isNaN(time) ? 0 : time
  }

  /** Newest first. */
  const sortedNotes = computed(() =>
    [...notes.value].sort((a, b) => timeOf(b) - timeOf(a)),
  )

  function reset() {
    // Bump the id so a read already in flight cannot repopulate after the clear.
    // That read then skips its own cleanup as superseded, so clear loading here
    // or the panel keeps its skeletons with nothing left to finish them.
    fetchRequestId++
    notes.value = []
    error.value = null
    merchantNotFound.value = false
    loading.value = false
  }

  async function fetchNotes(
    authState: AuthState,
    merchantId: string,
  ): Promise<SupplierNote[]> {
    const requestId = ++fetchRequestId
    loading.value = true
    error.value = null
    merchantNotFound.value = false
    try {
      const rows = await supplierNotesApi.getNotes(authState, merchantId)
      if (requestId === fetchRequestId) notes.value = rows
      return rows
    } catch (err) {
      const apiError = err as ApiError
      // A superseded read stays silent: its supplier is no longer on screen.
      if (requestId !== fetchRequestId) return []
      notes.value = []
      // Branch on the code, never the message text.
      if (apiError?.statusCode === 404) {
        merchantNotFound.value = true
        return []
      }
      error.value = apiError
      throw apiError
    } finally {
      if (requestId === fetchRequestId) loading.value = false
    }
  }

  /**
   * Shows the note before the write settles. The placeholder's negative id
   * cannot collide with a real `NOTE_ID`, and is swapped for the server's row
   * on success or dropped on failure.
   */
  async function addNote(
    authState: AuthState,
    merchantId: string,
    body: string,
    author: string,
  ): Promise<SupplierNote | null> {
    const wasAlreadySaving = saving.value
    saving.value = true
    if (wasAlreadySaving) return null

    const placeholder: SupplierNote = {
      NOTE_ID: -Date.now(),
      BODY: body,
      AUTHOR_EMAIL: author,
      CREATED_AT: new Date().toISOString(),
      UPDATED_AT: null,
    }
    notes.value = [placeholder, ...notes.value]

    try {
      const created = await supplierNotesApi.createNote(
        authState,
        merchantId,
        body,
      )
      notes.value = notes.value.map(note =>
        note.NOTE_ID === placeholder.NOTE_ID ? (created ?? note) : note,
      )
      return created
    } catch (err) {
      notes.value = notes.value.filter(
        note => note.NOTE_ID !== placeholder.NOTE_ID,
      )
      throw err as ApiError
    } finally {
      saving.value = false
    }
  }

  /** Applies the new body immediately, restoring the old one if the write fails. */
  async function editNote(
    authState: AuthState,
    merchantId: string,
    noteId: number,
    body: string,
  ): Promise<void> {
    const wasAlreadySaving = saving.value
    saving.value = true
    if (wasAlreadySaving) return

    const previous = notes.value.find(note => note.NOTE_ID === noteId)
    notes.value = notes.value.map(note =>
      note.NOTE_ID === noteId
        ? { ...note, BODY: body, UPDATED_AT: new Date().toISOString() }
        : note,
    )

    try {
      const updated = await supplierNotesApi.updateNote(
        authState,
        merchantId,
        noteId,
        body,
      )
      if (updated) {
        notes.value = notes.value.map(note =>
          note.NOTE_ID === noteId ? updated : note,
        )
      }
    } catch (err) {
      if (previous) {
        notes.value = notes.value.map(note =>
          note.NOTE_ID === noteId ? previous : note,
        )
      }
      throw err as ApiError
    } finally {
      saving.value = false
    }
  }

  /** Drops the note immediately, putting it back in place if the write fails. */
  async function removeNote(
    authState: AuthState,
    merchantId: string,
    noteId: number,
  ): Promise<void> {
    const wasAlreadySaving = saving.value
    saving.value = true
    if (wasAlreadySaving) return

    const requestId = fetchRequestId
    const index = notes.value.findIndex(note => note.NOTE_ID === noteId)
    const previous = index >= 0 ? notes.value[index] : null
    notes.value = notes.value.filter(note => note.NOTE_ID !== noteId)

    try {
      await supplierNotesApi.deleteNote(authState, merchantId, noteId)
    } catch (err) {
      // Restore only if the list is still the one this note came from: a read
      // for another supplier since then means splicing it back would insert a
      // stranger's note. The other rollbacks match on NOTE_ID and simply miss.
      if (previous && requestId === fetchRequestId) {
        const restored = [...notes.value]
        restored.splice(index, 0, previous)
        notes.value = restored
      }
      throw err as ApiError
    } finally {
      saving.value = false
    }
  }

  return {
    notes,
    sortedNotes,
    noteCount,
    loading,
    saving,
    error,
    merchantNotFound,
    reset,
    fetchNotes,
    addNote,
    editNote,
    removeNote,
  }
})
